import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useParams } from "@tanstack/react-router";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { docUrl } from "../lib/docLinks";
import { ApiRequestError } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { parseTraceDetailSearch } from "../lib/routerSearch";
import {
  firstTraceId,
  formatNanosecondsAsMs,
  spanStatusTone,
  traceTreeToWaterfallSpans,
} from "../lib/traces";
import {
  Badge,
  Code,
  DataTable,
  EmptyState,
  HelpTip,
  InlineError,
  KeyValue,
  Page,
  PageHeader,
  Section,
  Skeleton,
  Stack,
  TraceWaterfall,
} from "../ui";
import { useTypedSearch } from "../lib/typedRouter";

type StoredSpan = components["schemas"]["StoredSpan"];
type TraceSpansResponse = components["schemas"]["TraceSpansResponse"];

function traceErrorMessage(error: unknown): string | null {
  if (!error) {
    return null;
  }
  if (error instanceof ApiRequestError) {
    return `${error.status.code}: ${error.status.message}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Trace failed to load.";
}

const spanColumns: Array<ColumnDef<StoredSpan>> = [
  {
    header: "Span",
    cell: ({ row }) => <Code>{row.original.span_id}</Code>,
  },
  {
    accessorKey: "name",
    header: "Name",
  },
  {
    header: "Kind",
    cell: ({ row }) => <Badge>{row.original.kind}</Badge>,
  },
  {
    header: "Status",
    cell: ({ row }) => (
      <Badge tone={spanStatusTone(row.original.status_code)}>
        {row.original.status_code}
      </Badge>
    ),
  },
  {
    header: "Duration",
    cell: ({ row }) => formatNanosecondsAsMs(row.original.duration_ns),
  },
  {
    header: "Start",
    cell: ({ row }) => `${row.original.start_ns.toString()} ns`,
  },
];

export interface TraceDetailViewProps {
  activeSpanId?: string;
  error?: string | null;
  isPending?: boolean;
  trace: TraceSpansResponse | null;
}

export function TraceDetailView({
  activeSpanId,
  error,
  isPending = false,
  trace,
}: TraceDetailViewProps) {
  const traceId = trace ? firstTraceId(trace) : null;
  const flatSpans = trace?.spans ?? [];
  const tree = trace?.tree ?? [];
  const waterfallSpans = traceTreeToWaterfallSpans(tree);

  // Trace detail is a read-only Dossier in the workspace container. The
  // waterfall facet is unbounded-height and read together with the spans table,
  // so the page keeps document scroll; a fill-mode spans table would need the
  // waterfall to scroll internally (a restructure, not conformance — same
  // shape as the TranscriptTrace facet).
  return (
    <Page variant="workspace">
      <PageHeader
        description={
          trace ? `${trace.total_spans.toString()} stored spans` : undefined
        }
      />
      {isPending ? (
        <Stack gap={3}>
          <Skeleton rows={3} shape="detail-field" />
          <Skeleton rows={8} shape="table-row" />
        </Stack>
      ) : null}
      {error ? <InlineError>{error}</InlineError> : null}
      {!isPending && !error && !trace ? (
        <EmptyState title="Trace not found" />
      ) : null}
      {trace ? (
        <Stack gap={4}>
          <Section
            heading="Summary"
            help={
              <HelpTip
                docHref={docUrl("concepts/traces-and-notes")}
                docLabel="Traces and notes"
                label="About traces"
              >
                {
                  "A Trace holds the OpenTelemetry spans your agent emitted for a Transcript. It is not the Transcript itself."
                }
              </HelpTip>
            }
          >
            <KeyValue
              rows={[
                { label: "Trace", value: traceId ?? "-" },
                { label: "Spans", value: trace.total_spans.toString() },
                {
                  label: "Roots",
                  value: tree.length.toString(),
                },
              ]}
            />
          </Section>
          <Section heading="Waterfall">
            {waterfallSpans.length === 0 ? (
              <EmptyState title="No waterfall" />
            ) : (
              <TraceWaterfall
                activeSpanId={activeSpanId}
                spans={waterfallSpans}
              />
            )}
          </Section>
          <Section heading="Spans">
            {flatSpans.length === 0 ? (
              <EmptyState title="No spans" />
            ) : (
              <DataTable columns={spanColumns} data={flatSpans} />
            )}
          </Section>
        </Stack>
      ) : null}
    </Page>
  );
}

export function TraceDetail() {
  const { id } = useParams({
    from: "/authenticated/billing-gate/app-shell/traces/$id",
  });
  const search = useTypedSearch(parseTraceDetailSearch);
  const trace = useQuery({
    queryKey: queryKeys.traces.detail(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/traces/{id}", {
        params: { path: { id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  return (
    <TraceDetailView
      activeSpanId={search.span_id}
      error={traceErrorMessage(trace.error)}
      isPending={trace.isPending}
      trace={trace.data ?? null}
    />
  );
}
